import type { PromoteRequest } from "../../caseFiles/api/filesystemApi";
import { nodeFilename, nodeTitle } from "../../caseFiles/nodeNames";
import type { FsNode, MoveTarget } from "../../caseFiles/types";
import type { DropPlacement } from "../files/treeRows";

export type ExplorerTree = "files" | "workspace";

const DOCX_MIME =
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

/** A Word case file Casey can take on as a draft. `.doc` and `.rtf` stay put. */
export const isMovableDocx = (node: FsNode): boolean => {
  if (node.kind !== "file" || node.is_deleted) return false;
  const ext = (node.extension ?? node.format ?? "")
    .replace(/^\./, "")
    .toLowerCase();
  if (ext === "docx") return true;
  if (ext) return false;
  if (node.mime_type === DOCX_MIME) return true;
  return nodeFilename(node).toLowerCase().endsWith(".docx");
};

export type MoveWarning = {
  names: string[];
  skipped: number;
};

/** What the confirmation says about the Word files that will actually move. */
export const moveWarning = (
  moving: FsNode[],
  dragged: FsNode[]
): MoveWarning => ({
  names: moving.map((node) => nodeTitle(node) || "Untitled"),
  skipped: dragged.length - moving.length
});

export const moveWarningText = (warning: MoveWarning): string => {
  const count = warning.names.length;
  const lead =
    count === 1
      ? `"${warning.names[0]}" will leave Case Files to become a draft under Work Products that Casey can edit, so it is no longer a fixed record.`
      : `${count} Word documents will leave Case Files to become drafts under Work Products that Casey can edit, so they are no longer a fixed record.`;
  if (warning.skipped === 0) return lead;
  const stayed =
    warning.skipped === 1
      ? "1 other file stays in Case Files. Only Word documents can become drafts."
      : `${warning.skipped} other files stay in Case Files. Only Word documents can become drafts.`;
  return `${lead} ${stayed}`;
};

export const ONLY_DOCX_TOAST =
  "Only Word documents can move to Work Products. Casey edits those as drafts.";

export const ONLY_DRAFTS_AND_FORMS_TOAST =
  "Only drafts and forms can be added to Case Files.";

export type PromoteSourceInput = {
  kind: "draft" | "form";
  id: string;
  title: string;
};

/** Add to Case Files defaults: PDF, included in the packet, dropped folder. */
export const promoteRequestFor = (
  source: PromoteSourceInput,
  parentId: string | null
): PromoteRequest => ({
  source: { kind: source.kind, id: source.id },
  parent_id: parentId,
  title: source.title,
  format: "pdf",
  include: true
});

export type WorkspaceDragItem = {
  nodeId: string;
  kind: string;
  refId: string | null;
  title: string;
  /** A draft that already has a document, or a filled form. */
  copyable: boolean;
};

export const copyableWorkProducts = (
  items: WorkspaceDragItem[]
): { sources: PromoteSourceInput[]; rejected: WorkspaceDragItem[] } => {
  const sources: PromoteSourceInput[] = [];
  const rejected: WorkspaceDragItem[] = [];
  items.forEach((item) => {
    if (
      (item.kind === "draft" || item.kind === "form") &&
      item.copyable &&
      item.refId
    ) {
      sources.push({
        kind: item.kind,
        id: item.refId,
        title: item.title || "Untitled"
      });
      return;
    }
    if (item.kind !== "folder") rejected.push(item);
  });
  return { sources, rejected };
};

export const rejectCopyMessage = (rejected: WorkspaceDragItem[]): string => {
  if (
    rejected.length === 1 &&
    rejected[0].kind === "draft" &&
    !rejected[0].copyable
  ) {
    return `"${rejected[0].title || "This draft"}" has no document yet.`;
  }
  return ONLY_DRAFTS_AND_FORMS_TOAST;
};

export const positionOf = (placement: DropPlacement): MoveTarget => ({
  target_parent_id: placement.parentId,
  ...(placement.kind === "before" ? { before_id: placement.siblingId } : {}),
  ...(placement.kind === "after" ? { after_id: placement.siblingId } : {})
});

/** Dragged ids in the order they appear in the tree. */
export const inTreeOrder = (
  ids: string[],
  nodes: { id: string }[]
): string[] => {
  const index = new Map(nodes.map((node, i) => [node.id, i]));
  return [...ids].sort((a, b) => (index.get(a) ?? 0) - (index.get(b) ?? 0));
};
